import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";
import { Ppp } from "@/types";
import { Skeleton } from "@/components/ui/skeleton";
import { Layers } from "lucide-react";
import { PppRow } from "./PppRow";
import { PPP_ROW_COLS } from "./ppp-constants";

interface PppListProps {
  ppps: Ppp[];
  isLoading: boolean;
  isError: boolean;
  /** Leader and above: may open the edit form (BE `OKR_LEADER_ONLY`). */
  canEdit: boolean;
  /** Director and above: may drag to rank and delete (BE `OKR_DIRECTOR_ONLY`). */
  canRank: boolean;
  /** Ranking only makes sense on the full, unfiltered list. */
  hasFilters: boolean;
  /** False when the list is filtered or truncated: a row's position is then not its rank. */
  showRank: boolean;
  onDragEnd: (result: DropResult) => void;
  onEdit: (ppp: Ppp) => void;
  onDelete: (ppp: Ppp) => void;
}

const HEADER = ["#", "PPP", "Category", "Key Results", "DRI", "Deliverables", ""];

export function PppList({ ppps, isLoading, isError, canEdit, canRank, hasFilters, showRank, onDragEnd, onEdit, onDelete }: PppListProps) {
  const canDrag = canRank && !hasFilters;

  const header = (
    <div className="grid items-center gap-3 border-b bg-muted/30 px-4 py-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground" style={PPP_ROW_COLS}>
      {HEADER.map((h, i) => <div key={i}>{h}</div>)}
    </div>
  );

  if (isLoading) {
    return (
      <div className="overflow-hidden rounded-xl border bg-background">
        {header}
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="grid min-h-[56px] items-center gap-3 border-b border-border/40 px-4" style={PPP_ROW_COLS}>
            <Skeleton className="h-3 w-8" /><Skeleton className="h-4 w-56" /><Skeleton className="h-5 w-14" />
            <Skeleton className="h-4 w-32" /><Skeleton className="h-4 w-24" /><Skeleton className="h-3 w-20" /><div />
          </div>
        ))}
      </div>
    );
  }

  if (isError) {
    return <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-8 text-center text-sm text-destructive">Failed to load PPPs.</div>;
  }

  if (ppps.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed p-12 text-center text-muted-foreground">
        <Layers className="h-8 w-8 opacity-40" />
        <p className="text-sm">{hasFilters ? "No PPP matches these filters." : canEdit ? "No PPP yet — create the first Product, Partner or Project." : "No PPP yet."}</p>
      </div>
    );
  }

  const rows = ppps.map((ppp, index) =>
    canDrag ? (
      <Draggable key={ppp.pid} draggableId={ppp.pid} index={index}>
        {(provided, snapshot) => (
          <div ref={provided.innerRef} {...provided.draggableProps} className={snapshot.isDragging ? "bg-background shadow-lg" : undefined}>
            <PppRow ppp={ppp} rank={showRank ? index + 1 : null} canEdit={canEdit} canDelete={canRank} dragHandleProps={provided.dragHandleProps} onEdit={onEdit} onDelete={onDelete} />
          </div>
        )}
      </Draggable>
    ) : (
      <PppRow key={ppp.pid} ppp={ppp} rank={showRank ? index + 1 : null} canEdit={canEdit} canDelete={canRank} onEdit={onEdit} onDelete={onDelete} />
    ),
  );

  return (
    <div className="overflow-hidden rounded-xl border bg-background">
      {header}
      {canDrag ? (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="ppps" type="PPP">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {rows}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      ) : <div>{rows}</div>}
    </div>
  );
}
